/**
 * VerificationGroupsDataTable - Groups table using the reusable DataTable component
 */

// react
import { useCallback, useContext, useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// mui
import { alpha, Avatar, Chip, Stack, Typography, useTheme } from '@mui/material';

// icons
import {
	Delete as IconDelete,
	Edit as IconEdit,
	Group as IconGroup,
	Star as IconLead,
	Visibility as IconView,
} from '@mui/icons-material';

// context
import { ContextProject } from '../../Project/Project.context';

// components
import { ColumnDef, DataTable, RowAction, SortDirection } from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// types
import { VerificationGroup } from '../../../../@types';

// Types

interface VerificationGroupsDataTableProps {
	data: VerificationGroup[];
	isLoading?: boolean;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	orderBy?: string;
	order?: SortDirection;
	onSortChange?: (orderBy: string, order: SortDirection) => void;
	page?: number;
	rowsPerPage?: number;
	totalCount?: number;
	onPageChange?: (page: number) => void;
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	onEditGroup?: (group: VerificationGroup) => void;
	onDeactivateGroup?: (group: VerificationGroup) => void;
}

// Component

export const VerificationGroupsDataTable = ({
	data,
	isLoading = false,
	searchValue = '',
	onSearchChange,
	orderBy = 'name',
	order = 'ASC',
	onSortChange,
	page = 1,
	rowsPerPage = 10,
	totalCount = 0,
	onPageChange,
	onRowsPerPageChange,
	onEditGroup,
	onDeactivateGroup,
}: VerificationGroupsDataTableProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { project } = useContext(ContextProject);

	const handleRowClick = useCallback(
		(group: VerificationGroup) => {
			navigate(`/project/${project?.idWbg}/spend-plans/verification-group/${group.id}`);
		},
		[navigate, project]
	);

	// Column definitions
	const columns: ColumnDef<VerificationGroup>[] = useMemo(
		() => [
			{
				id: 'name',
				label: t('spendPlan.verificationGroups.groupName'),
				sortable: true,
				minWidth: 200,
				render: (row) => (
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Avatar
							sx={{
								bgcolor: alpha(theme.palette.primary.main, 0.1),
								width: 32,
								height: 32,
							}}>
							<IconGroup sx={{ fontSize: 16, color: 'primary.main' }} />
						</Avatar>
						<TruncatedText
							text={row.name}
							variant="body2"
							fontWeight={600}
							sx={{ maxWidth: 160 }}
						/>
					</Stack>
				),
			},
			{
				id: 'description',
				label: t('description'),
				sortable: true,
				minWidth: 200,
				render: (row) => (
					<TruncatedText
						text={row.description || '—'}
						variant="body2"
						color="text.secondary"
						sx={{ maxWidth: 400 }}
					/>
				),
			},
			{
				id: 'members',
				label: t('members'),
				sortable: true,
				align: 'center' as const,
				minWidth: 100,
				render: (row) => {
					const count = row.members?.length || 0;
					return (
						<Chip
							label={count}
							size="small"
							variant="outlined"
							sx={{ fontSize: 12, fontWeight: 600 }}
						/>
					);
				},
			},
			{
				id: 'leads',
				label: t('leads'),
				sortable: true,
				align: 'center' as const,
				minWidth: 80,
				render: (row) => {
					const leadCount = row.members?.filter((m) => m.role === 'LEAD').length || 0;
					return leadCount > 0 ? (
						<Chip
							icon={<IconLead sx={{ fontSize: 14 }} />}
							label={leadCount}
							size="small"
							color="primary"
							variant="outlined"
							sx={{ fontSize: 12 }}
						/>
					) : (
						<Typography variant="body2" color="text.secondary">
							—
						</Typography>
					);
				},
			},
			{
				id: 'created',
				label: t('created'),
				sortable: true,
				minWidth: 120,
				render: (row) => (
					<Typography variant="body2" color="text.secondary">
						{row.created
							? new Date(row.created).toLocaleDateString('en-US', {
									day: 'numeric',
									month: 'short',
									year: 'numeric',
								})
							: '—'}
					</Typography>
				),
			},
		],
		[theme]
	);

	// Row actions
	const rowActions: RowAction<VerificationGroup>[] = useMemo(
		() => [
			{
				label: 'View Details',
				icon: <IconView sx={{ fontSize: 18 }} />,
				onClick: handleRowClick,
			},
			{
				label: 'Edit Group',
				icon: <IconEdit sx={{ fontSize: 18 }} />,
				onClick: (row) => onEditGroup?.(row),
			},
			{
				label: 'Deactivate Group',
				icon: <IconDelete sx={{ fontSize: 18 }} />,
				onClick: (row) => onDeactivateGroup?.(row),
				destructive: true,
				dividerBefore: true,
			},
		],
		[handleRowClick, onEditGroup, onDeactivateGroup]
	);

	return (
		<DataTable<VerificationGroup>
			data={data}
			columns={columns}
			getRowKey={(row) => row.id}
			loading={isLoading}
			emptyMessage={t('spendPlan.verificationGroups.noGroupsYet')}
			emptyContent={t('spendPlan.verificationGroups.noGroupsDescription')}
			emptyIcon={<IconGroup sx={{ fontSize: 48 }} />}
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			searchPlaceholder={t('spendPlan.verificationGroups.searchPlaceholder')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			rowActions={rowActions}
			onRowClick={handleRowClick}
		/>
	);
};
